export const GROUP_TICKET_ERROR_CODES = Object.freeze({
  STALE_TICKET_CONTRACT: 'STALE_TICKET_CONTRACT',
  GROUP_INVENTORY_UNAVAILABLE: 'GROUP_INVENTORY_UNAVAILABLE',
  SEAT_INVENTORY_UNAVAILABLE: 'SEAT_INVENTORY_UNAVAILABLE',
  INVALID_PURCHASED_GROUP: 'INVALID_PURCHASED_GROUP',
  IDEMPOTENCY_CONFLICT: 'IDEMPOTENCY_CONFLICT',
});

export class GroupTicketContractError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = 'GroupTicketContractError';
    this.code = code;
    this.details = details;
  }
}

const fail = (code, message, details) => {
  throw new GroupTicketContractError(code, message, details);
};

const assertNonNegativeInteger = (value, field) => {
  if (!Number.isSafeInteger(value) || value < 0) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      `${field} must be a non-negative safe integer`,
      { field, value },
    );
  }
};

const assertSeatCount = (value, field = 'seatCount') => {
  if (!Number.isSafeInteger(value) || value < 1) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      `${field} must be a positive safe integer`,
      { field, value },
    );
  }
};

const assertGroupSeatThreshold = (value, field) => {
  if (!Number.isSafeInteger(value) || value < 2) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      `${field} must be a safe integer of at least 2`,
      { field, value },
    );
  }
};

const safeProduct = (left, right, field) => {
  const product = left * right;
  if (!Number.isSafeInteger(product)) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      `${field} exceeds the safe integer range`,
      { left, right },
    );
  }
  return product;
};

const assertGroupCounters = (ticket) => {
  assertNonNegativeInteger(ticket.quantity, 'quantity');
  assertNonNegativeInteger(ticket.sold, 'sold');
  assertNonNegativeInteger(ticket.seatCapacity, 'seatCapacity');
  assertNonNegativeInteger(ticket.seatsSold, 'seatsSold');
  const paused = ticket.ticketSalesPaused === true;
  const hasPauseFacts =
    ticket.ticketSalesPauseReason != null ||
    ticket.ticketSalesPausedAt != null ||
    ticket.ticketSalesPauseIncidentID != null;
  if (
    (paused &&
      (ticket.ticketSalesPauseReason !== 'LATE_REFUND_OVER_CAPACITY' ||
        typeof ticket.ticketSalesPausedAt !== 'string' ||
        !ticket.ticketSalesPausedAt ||
        typeof ticket.ticketSalesPauseIncidentID !== 'string' ||
        !ticket.ticketSalesPauseIncidentID)) ||
    (!paused && hasPauseFacts)
  ) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'ticket sales pause marker and incident facts must be set or cleared together',
      { ticketID: ticket.id },
    );
  }
  if (!paused && (ticket.sold > ticket.quantity || ticket.seatsSold > ticket.seatCapacity)) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'ticket counters exceed configured capacity',
      { ticketID: ticket.id },
    );
  }
};

export const normalizeGroupTicketConfiguration = (ticket) => {
  if (!ticket || ticket.type !== 'GROUP') {
    fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'expected a Group Ticket configuration');
  }

  if (ticket.groupSizeMode == null && ticket.pricingMode == null) {
    assertGroupSeatThreshold(ticket.seats, 'seats');
    assertNonNegativeInteger(ticket.quantity, 'quantity');
    assertNonNegativeInteger(ticket.sold, 'sold');
    if (ticket.sold > ticket.quantity) {
      fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'legacy sold count exceeds quantity', {
        ticketID: ticket.id,
      });
    }
    return {
      ...ticket,
      groupSizeMode: 'FIXED',
      pricingMode: 'FLAT',
      seatCapacity: safeProduct(ticket.quantity, ticket.seats, 'seatCapacity'),
      seatsSold: safeProduct(ticket.sold, ticket.seats, 'seatsSold'),
    };
  }

  if (ticket.groupSizeMode === 'FIXED') {
    assertGroupSeatThreshold(ticket.seats, 'seats');
    if ('minimumSeats' in ticket || 'maximumSeats' in ticket) {
      fail(
        GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
        'FIXED Group Tickets reject minimumSeats and maximumSeats',
        { ticketID: ticket.id },
      );
    }
    if (ticket.seatCapacity !== safeProduct(ticket.quantity, ticket.seats, 'seatCapacity')) {
      fail(
        GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
        'FIXED seatCapacity must equal quantity * seats',
        { ticketID: ticket.id },
      );
    }
  } else if (ticket.groupSizeMode === 'MINIMUM') {
    assertGroupSeatThreshold(ticket.minimumSeats, 'minimumSeats');
    if ('seats' in ticket) {
      fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'MINIMUM Group Tickets reject seats', {
        ticketID: ticket.id,
      });
    }
    if (ticket.minimumSeats > ticket.seatCapacity) {
      fail(
        GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
        'minimumSeats cannot exceed seatCapacity',
        { ticketID: ticket.id },
      );
    }
    if (ticket.maximumSeats != null) {
      assertSeatCount(ticket.maximumSeats, 'maximumSeats');
      if (ticket.maximumSeats < ticket.minimumSeats || ticket.maximumSeats > ticket.seatCapacity) {
        fail(
          GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
          'maximumSeats must be between minimumSeats and seatCapacity',
          { ticketID: ticket.id },
        );
      }
    }
  } else {
    fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'unknown groupSizeMode', {
      groupSizeMode: ticket.groupSizeMode,
    });
  }

  if (ticket.pricingMode !== 'FLAT' && ticket.pricingMode !== 'PER_SEAT') {
    fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'unknown pricingMode', {
      pricingMode: ticket.pricingMode,
    });
  }
  assertGroupCounters(ticket);
  return { ...ticket };
};

export const getGroupSeatBounds = (ticketInput) => {
  const ticket = normalizeGroupTicketConfiguration(ticketInput);
  const remainingSeats = ticket.seatCapacity - ticket.seatsSold;
  if (ticket.groupSizeMode === 'FIXED') {
    return { minimum: ticket.seats, maximum: ticket.seats, remainingSeats };
  }
  return {
    minimum: ticket.minimumSeats,
    maximum:
      ticket.maximumSeats == null ? remainingSeats : Math.min(ticket.maximumSeats, remainingSeats),
    remainingSeats,
  };
};

export const validateGroupSelection = ({ ticket: ticketInput, seatCount }) => {
  const ticket = normalizeGroupTicketConfiguration(ticketInput);
  assertSeatCount(seatCount);

  if (ticket.archived || ticket.ticketSalesPaused) {
    fail(GROUP_TICKET_ERROR_CODES.GROUP_INVENTORY_UNAVAILABLE, 'ticket sales are paused', {
      ticketID: ticket.id,
    });
  }

  if (ticket.sold >= ticket.quantity) {
    fail(GROUP_TICKET_ERROR_CODES.GROUP_INVENTORY_UNAVAILABLE, 'no group units remain', {
      ticketID: ticket.id,
    });
  }
  if (ticket.seatsSold + seatCount > ticket.seatCapacity) {
    fail(
      GROUP_TICKET_ERROR_CODES.SEAT_INVENTORY_UNAVAILABLE,
      'insufficient physical seats remain',
      { ticketID: ticket.id },
    );
  }
  if (ticket.groupSizeMode === 'FIXED' && seatCount !== ticket.seats) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'FIXED group size must equal configured seats',
      { ticketID: ticket.id, seatCount },
    );
  }
  if (
    ticket.groupSizeMode === 'MINIMUM' &&
    (seatCount < ticket.minimumSeats ||
      (ticket.maximumSeats != null && seatCount > ticket.maximumSeats))
  ) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'MINIMUM group size is outside configured bounds',
      { ticketID: ticket.id, seatCount },
    );
  }
  return { ticket, seatCount };
};

export const calculatePurchasedGroupAmounts = ({
  pricingMode,
  unitPriceMinor,
  seatCount,
  promotion = null,
}) => {
  if (pricingMode !== 'FLAT' && pricingMode !== 'PER_SEAT') {
    fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'unknown pricingMode', { pricingMode });
  }
  assertNonNegativeInteger(unitPriceMinor, 'unitPriceMinor');
  assertSeatCount(seatCount);

  const subtotalAmountMinor =
    pricingMode === 'FLAT'
      ? unitPriceMinor
      : safeProduct(unitPriceMinor, seatCount, 'subtotalAmountMinor');
  const discountAmountMinor = calculateGroupPromotionDiscount({
    subtotalAmountMinor,
    promotion,
  });
  return {
    unitPriceMinor,
    subtotalAmountMinor,
    discountAmountMinor,
    totalAmountMinor: subtotalAmountMinor - discountAmountMinor,
  };
};

export const calculateGroupPromotionDiscount = ({ subtotalAmountMinor, promotion = null }) => {
  assertNonNegativeInteger(subtotalAmountMinor, 'subtotalAmountMinor');
  if (promotion == null) return 0;
  if (promotion.type === 'FIXED') {
    assertNonNegativeInteger(promotion.amountMinor, 'promotion.amountMinor');
    return Math.min(promotion.amountMinor, subtotalAmountMinor);
  }
  if (promotion.type === 'PERCENTAGE') {
    assertNonNegativeInteger(promotion.basisPoints, 'promotion.basisPoints');
    if (promotion.basisPoints > 10_000) {
      fail(
        GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
        'promotion.basisPoints cannot exceed 10000',
      );
    }
    const scaledDiscount = safeProduct(
      subtotalAmountMinor,
      promotion.basisPoints,
      'percentage discount',
    );
    return Math.floor((scaledDiscount + 5_000) / 10_000);
  }
  fail(GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP, 'unknown promotion type', {
    promotionType: promotion.type,
  });
};

export const applyGroupInventoryDelta = ({ before, delta }) => {
  assertNonNegativeInteger(before.sold, 'before.sold');
  assertNonNegativeInteger(before.seatsSold, 'before.seatsSold');
  if (!Number.isSafeInteger(delta.groupUnits) || !Number.isSafeInteger(delta.seats)) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'inventory deltas must be safe integers',
    );
  }
  const after = {
    sold: before.sold + delta.groupUnits,
    seatsSold: before.seatsSold + delta.seats,
  };
  assertNonNegativeInteger(after.sold, 'after.sold');
  assertNonNegativeInteger(after.seatsSold, 'after.seatsSold');
  return after;
};

export const applyTicketInventoryOperation = ({ before, operation }) => {
  const attendees = before.attendees.map((attendee) => ({
    ...attendee,
    releaseReasons: [...attendee.releaseReasons],
  }));
  const appliedOperationKeys = [...before.appliedOperationKeys];
  const replay = appliedOperationKeys.includes(operation.operationKey);
  let newlyReleasedSeats = 0;
  let newlyReacquiredSeats = 0;

  if (!replay) {
    const selected = new Set(operation.confirmationIDs);
    for (const attendee of attendees) {
      if (!selected.has(attendee.confirmationID)) continue;
      const wasReleased = attendee.releaseReasons.length > 0;
      if (operation.releaseReason != null) {
        if (!attendee.releaseReasons.includes(operation.releaseReason)) {
          attendee.releaseReasons.push(operation.releaseReason);
        }
      } else if (operation.removeReleaseReason != null) {
        attendee.releaseReasons = attendee.releaseReasons.filter(
          (reason) => reason !== operation.removeReleaseReason,
        );
      } else {
        fail(
          GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
          'inventory operation requires a release or reacquisition reason',
        );
      }
      const isReleased = attendee.releaseReasons.length > 0;
      if (!wasReleased && isReleased) newlyReleasedSeats += 1;
      if (wasReleased && !isReleased) newlyReacquiredSeats += 1;
    }
    appliedOperationKeys.push(operation.operationKey);
  }

  const groupReleased =
    attendees.length > 0 && attendees.every(({ releaseReasons }) => releaseReasons.length > 0);
  const newlyReleasedGroups = !replay && !before.groupReleased && groupReleased ? 1 : 0;
  const newlyReacquiredGroups = !replay && before.groupReleased && !groupReleased ? 1 : 0;
  return {
    state: { attendees, appliedOperationKeys, groupReleased },
    effects: {
      newlyReleasedSeats,
      newlyReleasedGroups,
      newlyReacquiredSeats,
      newlyReacquiredGroups,
      groupReleased,
    },
  };
};

export const applyProviderCompensationTransition = ({ operation, before, observation }) => {
  if (operation === 'PAYMENT') {
    if (before === 'PREPARED' && observation === 'OUTBOUND_STARTED') {
      return { after: 'OUTCOME_UNKNOWN', inventoryOperation: null };
    }
    if (before === 'OUTCOME_UNKNOWN' && observation === 'TRANSPORT_TIMEOUT') {
      return { after: 'OUTCOME_UNKNOWN', inventoryOperation: null };
    }
    if (before === 'PROCESSOR_PENDING' && observation === 'VERIFIED_FAILED') {
      return { after: 'FAILED', inventoryOperation: 'PAYMENT_RELEASE:<transactionID>' };
    }
    if (before === 'SUCCEEDED' && observation === 'VERIFIED_FAILED') {
      return { after: 'NEEDS_REMEDIATION', inventoryOperation: null };
    }
  }
  if (operation === 'REFUND') {
    if (before === 'PROCESSOR_PENDING' && observation === 'VERIFIED_SUCCEEDED') {
      return { after: 'SUCCEEDED', inventoryOperation: 'REFUND_RELEASE:<refundRequestID>' };
    }
    if (before === 'SUCCEEDED' && observation === 'WEBHOOK_FAILED') {
      return { after: 'SUCCEEDED', inventoryOperation: null, requiredFollowUp: 'QUERY_PROVIDER' };
    }
    if (before === 'SUCCEEDED' && observation === 'VERIFIED_FAILED') {
      return { after: 'FAILED', inventoryOperation: 'REFUND_REACQUIRE:<refundRequestID>' };
    }
  }
  fail(
    GROUP_TICKET_ERROR_CODES.IDEMPOTENCY_CONFLICT,
    'provider observation is invalid for the current operation state',
    { operation, before, observation },
  );
};

export const deriveLegacyTicketMapping = (purchasedGroups, fractionDigits = 2) => {
  assertNonNegativeInteger(fractionDigits, 'fractionDigits');
  const divisor = 10 ** fractionDigits;
  if (!Number.isSafeInteger(divisor)) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'fractionDigits exceeds safe integer range',
    );
  }
  const byTicket = new Map();
  for (const group of purchasedGroups) {
    assertNonNegativeInteger(group.subtotalAmountMinor, 'subtotalAmountMinor');
    const current = byTicket.get(group.ticketID) ?? {
      ticketID: group.ticketID,
      ticketName: group.ticketName,
      numSold: 0,
      soldTotalPriceMinor: 0,
    };
    current.numSold += 1;
    current.soldTotalPriceMinor += group.subtotalAmountMinor;
    byTicket.set(group.ticketID, current);
  }
  return Array.from(byTicket.values()).map(({ soldTotalPriceMinor, ...entry }) => ({
    ...entry,
    soldTotalPrice: soldTotalPriceMinor / divisor,
  }));
};

const attendeeName = (attendee) =>
  [attendee.firstName, attendee.lastName].filter(Boolean).join(' ').trim();

/**
 * Projects immutable v2 purchased groups for readers while retaining a v1
 * flat-attendee fallback. This function never reconstructs v2 group identity
 * by chunking attendees.
 */
export const projectTicketPurchaseLineItems = (transaction, fractionDigits = 2) => {
  assertNonNegativeInteger(fractionDigits, 'fractionDigits');
  const divisor = 10 ** fractionDigits;
  if (!Number.isSafeInteger(divisor)) {
    fail(
      GROUP_TICKET_ERROR_CODES.INVALID_PURCHASED_GROUP,
      'fractionDigits exceeds safe integer range',
    );
  }
  const tickets = Array.isArray(transaction?.tickets) ? transaction.tickets : [];
  const purchasedGroups = Array.isArray(transaction?.purchasedGroups)
    ? transaction.purchasedGroups
    : [];

  if (transaction?.ticketContractVersion === 2 && purchasedGroups.length > 0) {
    const groupedTicketIDs = new Set(purchasedGroups.map(({ ticketID }) => ticketID));
    const groupLines = purchasedGroups.map((group) => {
      const attendees = tickets.filter(
        (ticket) => ticket.type === 'GROUP' && ticket.purchasedGroupID === group.purchasedGroupID,
      );
      return {
        key: group.purchasedGroupID,
        sourceVersion: 2,
        ticketID: group.ticketID,
        ticketName: group.ticketName,
        purchasedGroupID: group.purchasedGroupID,
        seatCount: group.seatCount,
        quantity: group.seatCount,
        groupUnits: 1,
        amount: group.totalAmountMinor / divisor,
        amountKnown: true,
        subtotal: group.subtotalAmountMinor / divisor,
        discount: group.discountAmountMinor / divisor,
        attendees,
        attendeeNames: attendees.map(attendeeName).filter(Boolean),
        ticketInventoryReleased: group.ticketInventoryReleased === true,
      };
    });

    const mappingByID = new Map(
      (Array.isArray(transaction?.ticketMapping) ? transaction.ticketMapping : []).map(
        (mapping) => [mapping.id, mapping],
      ),
    );
    const individualAttendees = new Map();
    for (const attendee of tickets.filter(({ type }) => type !== 'GROUP')) {
      const key = attendee.ticketID || attendee.ticketName;
      const current = individualAttendees.get(key) ?? [];
      current.push(attendee);
      individualAttendees.set(key, current);
    }
    const individualIDs = new Set([
      ...Array.from(mappingByID.keys()).filter((ticketID) => !groupedTicketIDs.has(ticketID)),
      ...individualAttendees.keys(),
    ]);
    const individualLines = Array.from(individualIDs).map((ticketID) => {
      const mapping = mappingByID.get(ticketID);
      const attendees = individualAttendees.get(ticketID) ?? [];
      const amount = mapping
        ? Number(mapping.soldTotalPrice)
        : attendees.reduce((sum, attendee) => sum + (Number(attendee.price) || 0), 0);
      const amountKnown =
        mapping != null || attendees.every((attendee) => attendee.price !== undefined);
      return {
        key: `individual:${ticketID}`,
        sourceVersion: 2,
        ticketID,
        ticketName: mapping?.name ?? attendees[0]?.ticketName ?? '',
        purchasedGroupID: null,
        seatCount: null,
        quantity: mapping?.numSold ?? attendees.length,
        groupUnits: 0,
        amount,
        amountKnown,
        subtotal: amount,
        discount: 0,
        attendees,
        attendeeNames: attendees.map(attendeeName).filter(Boolean),
        ticketInventoryReleased: false,
      };
    });
    return [...groupLines, ...individualLines];
  }

  const legacyMappings = Array.isArray(transaction?.ticketMapping)
    ? transaction.ticketMapping.filter((mapping) => Number(mapping.numSold) > 0)
    : [];
  if (legacyMappings.length > 0) {
    return legacyMappings.map((mapping) => {
      const attendees = tickets.filter(
        (ticket) => (ticket.ticketID || ticket.ticketName) === mapping.id,
      );
      const amount = Number(mapping.soldTotalPrice) || 0;
      return {
        key: `legacy:${mapping.id}`,
        sourceVersion: 1,
        ticketID: mapping.id,
        ticketName: mapping.name,
        purchasedGroupID: null,
        seatCount: null,
        quantity: Number(mapping.numSold),
        groupUnits: 0,
        amount,
        amountKnown: true,
        subtotal: amount,
        discount: 0,
        attendees,
        attendeeNames: attendees.map(attendeeName).filter(Boolean),
        ticketInventoryReleased: false,
      };
    });
  }

  const legacyGroupLines = [];
  const individualTickets = [];
  const groupTickets = new Map();
  for (const ticket of tickets) {
    const isGroup = ticket.type === 'GROUP' || Number(ticket.seats) > 1;
    if (!isGroup) {
      individualTickets.push(ticket);
      continue;
    }
    const key = ticket.ticketID || ticket.ticketName;
    const current = groupTickets.get(key) ?? [];
    current.push(ticket);
    groupTickets.set(key, current);
  }
  for (const [key, groupAttendees] of groupTickets) {
    const seats = Math.max(Number(groupAttendees[0]?.seats) || 1, 1);
    for (let offset = 0; offset < groupAttendees.length; offset += seats) {
      const attendees = groupAttendees.slice(offset, offset + seats);
      const amount = Number(attendees[0]?.price) || 0;
      legacyGroupLines.push({
        key: `legacy:${key}:${offset / seats}`,
        sourceVersion: 1,
        ticketID: groupAttendees[0]?.ticketID ?? key,
        ticketName: groupAttendees[0]?.ticketName ?? '',
        purchasedGroupID: null,
        seatCount: attendees.length,
        quantity: attendees.length,
        groupUnits: 1,
        amount,
        amountKnown: attendees[0]?.price !== undefined,
        subtotal: amount,
        discount: 0,
        attendees,
        attendeeNames: attendees.map(attendeeName).filter(Boolean),
        ticketInventoryReleased: false,
      });
    }
  }

  const groupedIndividuals = new Map();
  for (const ticket of individualTickets) {
    const key = ticket.ticketID || ticket.ticketName;
    const current = groupedIndividuals.get(key) ?? {
      key: `legacy:${key}`,
      sourceVersion: 1,
      ticketID: ticket.ticketID ?? key,
      ticketName: ticket.ticketName ?? '',
      purchasedGroupID: null,
      seatCount: null,
      quantity: 0,
      groupUnits: 0,
      amount: 0,
      amountKnown: true,
      subtotal: 0,
      discount: 0,
      attendees: [],
      attendeeNames: [],
      ticketInventoryReleased: false,
    };
    current.quantity += 1;
    current.amount += Number(ticket.price) || 0;
    current.subtotal += Number(ticket.price) || 0;
    current.attendees.push(ticket);
    const name = attendeeName(ticket);
    if (name) current.attendeeNames.push(name);
    groupedIndividuals.set(key, current);
  }
  return [...legacyGroupLines, ...groupedIndividuals.values()];
};
